class PaginationAjax extends HTMLElement {
  constructor() {
    super();

    this.handlePaginationClick = this.handlePaginationClick.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    this.section = this.dataset.section || "collection";
    this.isLoading = false;
    this.intersectionObserver = null;
    this.loadingIndicator = this.querySelector("[pagination-loading]");

    this.checkHistoryAndScroll();
    this.addEventListener("click", this.handlePaginationClick);

    this.setupInfiniteScroll();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handlePaginationClick);
    this.intersectionObserver?.disconnect();
  }

  handlePaginationClick(event) {
    const link = event.target.closest("a[paginationPrevious]");
    if (!link) {
      return;
    }

    event.preventDefault();
    this.paginationLink("previous", link.href);
  }

  async paginationLink(type, url) {
    if (this.isLoading) {
      return;
    }

    this.setLoadingState(true, type);

    try {
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set("section_id", this.section);

      const response = await fetch(urlObj.toString(), {
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) throw new Error("Network response was not ok");
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const newProductCards = doc.querySelectorAll("#product-grid > li");
      this.renderProductCards(type, newProductCards);
      window.history.pushState({ path: url }, "", url);
      this.updatePaginationLinks(type, doc);

      window.dispatchEvent(new CustomEvent("custom:facets:fetch:complete"));
      window.dispatchEvent(new CustomEvent("custom:pagination:refresh"));

      newProductCards[0]?.querySelector("product-card")?.focus();
    } catch (error) {
      console.error("Failed to fetch new page:", error);
    } finally {
      this.setLoadingState(false, type);
    }
  }

  renderProductCards(type, cards) {
    const productGrid = this.querySelector("#product-grid");
    if (!productGrid) {
      return;
    }

    if (type === "next") {
      cards.forEach((card) => productGrid.appendChild(card));
      return;
    }

    [...cards].reverse().forEach((card) => productGrid.prepend(card));
  }

  updatePaginationLinks(type, doc) {
    const prevLink = this.querySelector("[paginationPrevious]");
    const nextContainer = this.querySelector("[pagination-next]");
    const newNextContainer = doc.querySelector("[pagination-next]");
    const newNextUrl = newNextContainer?.dataset.nextUrl;
    const newPrevLink = doc.querySelector("[paginationPrevious]");

    if (type === "next") {
      if (nextContainer) {
        if (newNextUrl) {
          nextContainer.dataset.nextUrl = newNextUrl;
        } else {
          this.removePaginationEnd(nextContainer);
        }
      }
    } else {
      if (prevLink && newPrevLink) {
        prevLink.setAttribute("href", newPrevLink.getAttribute("href"));
      } else if (prevLink) {
        prevLink.parentNode.remove();
      }
    }

    this.refreshInfiniteScrollTarget();
  }

  removePaginationEnd(element) {
    element?.remove();
  }

  checkHistoryAndScroll() {
    const stateData = window.history.state || {};
    if (!stateData.collection_item_id) {
      return;
    }

    window.history.scrollRestoration = "manual";
    this.querySelector(
      `[product-item][data-id="${stateData.collection_item_id}"]`,
    )?.scrollIntoView({ block: "center" });
    delete stateData.collection_item_id;
    window.history.replaceState(stateData, "");
  }

  setupInfiniteScroll() {
    if (!("IntersectionObserver" in window)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
      root: null,
      rootMargin: "0px 0px 300px 0px",
      threshold: 0,
    });

    this.refreshInfiniteScrollTarget();
  }

  refreshInfiniteScrollTarget() {
    if (!this.intersectionObserver) {
      return;
    }

    this.intersectionObserver.disconnect();

    const nextContainer = this.querySelector("[pagination-next]");
    if (!nextContainer) {
      return;
    }

    this.intersectionObserver.observe(nextContainer);
  }

  onIntersect(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting || this.isLoading) {
        continue;
      }

      const nextContainer = this.querySelector("[pagination-next]");
      const url = nextContainer?.dataset.nextUrl;
      if (!url) {
        this.intersectionObserver?.disconnect();
        return;
      }

      this.paginationLink("next", url);
      break;
    }
  }

  setLoadingState(isLoading, type) {
    this.isLoading = isLoading;

    if (isLoading) {
      this.classList.add("pointer-events-none");
    } else {
      this.classList.remove("pointer-events-none");
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.hidden = !isLoading || type !== "next";
    }
  }
}

customElements.define("pagination-ajax", PaginationAjax);
